import "media-chrome/menu";
import "./component-menu-link";
import "./component-share-menu";
import "./component-overlay-button";

import composePlayPauseButton from "./compose-play-pause-button";
import composeMuteButton from "./compose-mute-button";
import resolveEnvironment from "./resolve-environment";
import resolvePlayerName from "./resolve-player-name";

/**
 * @typedef {import('../../types').Content} Content
 * @typedef {import('../../types').UserOptions} UserOptions
 */

const defaultCustomButtonIcon = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke-width='1.5' stroke='white'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25' /%3E%3C/svg%3E`;

/**
 * @param {UserOptions | any} userOptions - user settings passed into the component. Having to include `any` because there's no easy way of passing only attributes of the class without explicity stating what they are
 * @param {Content} mediaItem - the media item returned from the api
 * @returns {string} - the html string to be rendered
 */
function composeAudioHtml(userOptions, mediaItem) {
  // As well as destructuring to avoid more verbose code, we're also removing these from the userOptions object
  const {
    host,
    token,
    mediaplaying,
    aspectratio,
    islikelive,
    showdescription,
    overridedescription,
    showplaybackratecontrols,
    ismuted,
    shouldautostart,
    skipto,
    canfloat,
    width,
    showcontrols,
    skiprecap,
    skipintro,
    showtitle,
    overridetitle,
    overridethumbnail,
    showbrandlogo,
    showsharingbuttons,
    canbecast,
    isdownloadable,
    showpictureinpicturecontrols,
    overridecolor,
    environment,
    playercode,
    debug,
    beaconcollectiondomain,
    custombuttonimage,
    custombuttontitle,
    custombuttonurl,
    ...remainingUserOptions
  } = userOptions;

  const userOptionsString = Object.entries(remainingUserOptions)
    .map(([key, value]) => `metadata-${key}="${value}"`)
    .join(", ");

  const renderLiveChrome = islikelive === "true" || mediaItem.is_live;

  const shouldShowDescription =
    showdescription === "true" &&
    (mediaItem.description || overridedescription);

  const playerName = resolvePlayerName(playercode);
  const { env_key } = resolveEnvironment(environment);

  const mediaElement = `
    <mux-video
      slot="media"
      crossorigin
      ${ismuted === "true" ? "muted" : ""}
      src="${mediaItem.media?.playlist}"
      start-time="${skipto}"
      ${shouldautostart === "true" ? `autoplay` : ""}
      metadata-video-title="${overridetitle ?? mediaItem.title}"
      metadata-video-id="${token}"
      metadata-player-name="${playerName}"
      env-key="${env_key}"
      ${debug === "true" ? "debug" : ""}
      ${beaconcollectiondomain && `beacon-collection-domain=${beaconcollectiondomain}`}
      ${userOptionsString}
    >
      ${
        mediaItem.media?.tracks?.captions
          ? `
        ${mediaItem.media?.tracks?.captions?.map(
          (caption) => `
          <track label="${caption.name}" kind="captions" srclang="en" src="${caption.original_url}" />
        `,
        )}`
          : ""
      }
      ${mediaItem.media?.tracks?.chapters?.length ? `<track kind="chapters" src="${mediaItem.media?.tracks?.chapters}" default></track>` : ""}
      ${mediaItem.media?.tracks?.storyboard ? `<track default kind="metadata" label="thumbnails" src="${mediaItem.media?.tracks?.storyboard}">` : ""}
    </mux-video>
  `;

  const mediaSettingsMenu = `
    <media-settings-menu hidden anchor="auto">
      ${
        !renderLiveChrome && userOptions.showplaybackratecontrols === "true"
          ? `
        <media-settings-menu-item>
          Speed
          <media-playback-rate-menu slot="submenu" hidden>
            <div slot="title">Speed</div>
          </media-playback-rate-menu>
        </media-settings-menu-item>
      `
          : ""
      }
      ${
        mediaItem.media?.tracks?.captions?.length
          ? `
        <media-settings-menu-item>
          Captions
          <media-captions-menu slot="submenu" hidden>
          <div slot="title">Captions</div>
          </media-captions-menu>
        </media-settings-menu-item>
      `
          : ""
      }
    </media-settings-menu>
  `;

  const controlBar = `
  ${
    !renderLiveChrome
      ? `
      <media-time-range></media-time-range>
    `
      : ``
  }
  <div class="controls">
      <div>
        ${composePlayPauseButton("#ffffff")}
        ${
          !renderLiveChrome
            ? `
        <div class="time-duration-display">
          <media-time-display></media-time-display>
          <span>/</span>
          <media-duration-display></media-duration-display>
        </div>
        ${mediaItem.is_live ? `<media-live-button></media-live-button>` : ""}
        `
            : ""
        }
        
        ${
          mediaItem.is_live && mediaItem.status && mediaItem.status !== "Active"
            ? `
          <div class="offline-notice"><h2>Stream is not active</h2></div>
        `
            : ``
        }
        <div class="volume-controls">
          ${composeMuteButton(null)}
          <media-volume-range></media-volume-range>
        </div>
        </div>
        <div>
        <media-settings-menu-button notooltip>
          <svg slot="icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10.2745 22C9.96896 22 9.70087 21.9 9.47021 21.7C9.23971 21.5 9.10221 21.25 9.05771 20.95L8.73271 18.8333C8.44937 18.7278 8.15912 18.5889 7.86196 18.4167C7.56479 18.2444 7.29671 18.0638 7.05771 17.875L5.10771 18.7583C4.81338 18.8861 4.51896 18.9 4.22446 18.8C3.92996 18.7 3.69938 18.5139 3.53271 18.2418L2.17446 15.825C2.00779 15.5528 1.96054 15.2668 2.03271 14.9668C2.10504 14.6668 2.26621 14.4195 2.51621 14.225L4.29121 12.9082C4.26338 12.7694 4.24671 12.6181 4.24121 12.4543C4.23554 12.2903 4.23271 12.1388 4.23271 12C4.23271 11.8612 4.23554 11.7098 4.24121 11.5457C4.24671 11.3819 4.26338 11.2306 4.29121 11.0918L2.51621 9.775C2.26621 9.5805 2.10504 9.33325 2.03271 9.03325C1.96054 8.73325 2.00779 8.44717 2.17446 8.175L3.53271 5.75825C3.69938 5.48608 3.92996 5.3 4.22446 5.2C4.51896 5.1 4.81338 5.11392 5.10771 5.24175L7.05771 6.125C7.29671 5.93617 7.56621 5.75558 7.86621 5.58325C8.16621 5.41108 8.45504 5.27775 8.73271 5.18325L9.05771 3.05C9.10221 2.75 9.23971 2.5 9.47021 2.3C9.70087 2.1 9.96896 2 10.2745 2H13.0245C13.33 2 13.598 2.1 13.8287 2.3C14.0592 2.5 14.1967 2.75 14.2412 3.05L14.5662 5.16675C14.8495 5.27225 15.1425 5.40833 15.4452 5.575C15.748 5.74167 16.0134 5.925 16.2412 6.125L18.1912 5.24175C18.4855 5.11392 18.7772 5.1 19.0662 5.2C19.355 5.3 19.5828 5.48608 19.7495 5.75825L21.1245 8.175C21.2911 8.44717 21.3384 8.73608 21.2662 9.04175C21.1939 9.34725 21.0327 9.59167 20.7827 9.775L19.0077 11.0583C19.0355 11.2083 19.0522 11.3653 19.0577 11.5293C19.0634 11.6931 19.0662 11.85 19.0662 12C19.0662 12.15 19.0634 12.3042 19.0577 12.4625C19.0522 12.6208 19.0328 12.775 18.9995 12.925L20.7745 14.225C21.0245 14.4083 21.1855 14.6527 21.2577 14.9582C21.33 15.2639 21.2829 15.5528 21.1162 15.825L19.7495 18.2418C19.5828 18.5139 19.3522 18.7 19.0577 18.8C18.7634 18.9 18.469 18.8861 18.1745 18.7583L16.2412 17.875C16.0022 18.0638 15.7355 18.2472 15.4412 18.425C15.1467 18.6028 14.855 18.7389 14.5662 18.8333L14.2412 20.95C14.1967 21.25 14.0592 21.5 13.8287 21.7C13.598 21.9 13.33 22 13.0245 22H10.2745ZM11.6662 15.3332C12.5884 15.3332 13.3745 15.0083 14.0245 14.3582C14.6745 13.7083 14.9995 12.9222 14.9995 12C14.9995 11.0778 14.6745 10.2917 14.0245 9.64175C13.3745 8.99175 12.5884 8.66675 11.6662 8.66675C10.7384 8.66675 9.95087 8.99175 9.30371 9.64175C8.65638 10.2917 8.33271 11.0778 8.33271 12C8.33271 12.9222 8.65638 13.7083 9.30371 14.3582C9.95087 15.0083 10.7384 15.3332 11.6662 15.3332Z" fill="currentColor"/>
          </svg>
        </media-settings-menu-button>
        ${
          userOptions.showsharingbuttons === "true"
            ? `
          <share-menu iconColor="#71717a"></share-menu>
        `
            : ""
        }
        ${
          (mediaItem.player?.button?.url || custombuttonurl) && showbrandlogo !== "false"
            ? `
          <menu-link
            showTooltip="true"
            customtitle="${custombuttontitle ?? mediaItem.player?.button?.title ?? ""}"
            customimage="${defaultCustomButtonIcon}"
            customurl="${custombuttonurl ?? mediaItem.player?.button?.url}"
          ></menu-link>
        `
            : ""
        }
      ${
        userOptions.canbecast === "true"
          ? `
        <media-cast-button notooltip>
        <svg slot="icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M22 18.3093C22 18.7668 21.837 19.1586 21.5111 19.4846C21.1853 19.8104 20.7936 19.9732 20.336 19.9732H15.702C15.5057 19.9732 15.3409 19.8992 15.2076 19.7511C15.0744 19.603 15.0007 19.4302 14.9867 19.2327C14.909 17.6411 14.561 16.145 13.9425 14.7445C13.3242 13.344 12.5057 12.0995 11.4871 11.011C10.4686 9.9225 9.28172 9.02958 7.92632 8.33224C6.57109 7.63491 5.10871 7.20022 3.53917 7.02818C3.11771 6.97826 2.76139 6.81112 2.47021 6.52677C2.17903 6.24224 2.03344 5.88867 2.03344 5.46604C2.03344 5.04325 2.18244 4.69326 2.48045 4.41605C2.77845 4.13868 3.13693 4 3.55589 4H20.336C20.7936 4 21.1853 4.16289 21.5111 4.48868C21.837 4.81464 22 5.2064 22 5.66396V18.3093ZM7.8492 19.9732C7.63024 19.9732 7.43872 19.9063 7.27467 19.7723C7.11044 19.6384 7.00778 19.4585 6.96668 19.2327C6.80595 18.1179 6.3359 17.1695 5.55654 16.3875C4.77735 15.6055 3.83592 15.1341 2.73227 14.9734C2.52695 14.9401 2.35915 14.8386 2.22887 14.6689C2.09858 14.499 2.03344 14.3055 2.03344 14.0884C2.03344 13.8574 2.10549 13.6602 2.24958 13.4966C2.39384 13.333 2.56855 13.2623 2.7737 13.2844C4.32677 13.4508 5.64822 14.0887 6.73806 15.198C7.82791 16.3071 8.46434 17.6382 8.64737 19.1913C8.68064 19.4108 8.61409 19.596 8.4477 19.7469C8.28131 19.8978 8.08181 19.9732 7.8492 19.9732ZM11.8503 19.9732C11.6228 19.9732 11.4273 19.8984 11.2637 19.7486C11.1002 19.5989 11.0072 19.4103 10.985 19.1828C10.7853 16.9865 9.92148 15.1146 8.39354 13.5672C6.8656 12.0198 5.00896 11.1491 2.82362 10.9551C2.59068 10.9328 2.40075 10.8382 2.25382 10.6713C2.1069 10.5043 2.03344 10.3094 2.03344 10.0868C2.03344 9.85037 2.1208 9.6517 2.2955 9.4908C2.47021 9.32991 2.67678 9.2606 2.91522 9.2829C5.52485 9.49738 7.74546 10.5285 9.57706 12.3762C11.4087 14.2241 12.4326 16.4597 12.6489 19.083C12.671 19.3326 12.6024 19.5434 12.443 19.7154C12.2836 19.8873 12.086 19.9732 11.8503 19.9732ZM3.07919 19.9732C2.7757 19.9732 2.51996 19.8685 2.31198 19.659C2.10399 19.4494 2 19.1928 2 18.8893C2 18.5858 2.10482 18.3301 2.31447 18.1221C2.52396 17.9141 2.78044 17.8101 3.08393 17.8101C3.38743 17.8101 3.64316 17.9149 3.85115 18.1246C4.05913 18.3341 4.16313 18.5906 4.16313 18.8941C4.16313 19.1975 4.05839 19.4533 3.8489 19.6613C3.63925 19.8693 3.38268 19.9732 3.07919 19.9732Z" fill="currentColor"/>
          </svg>
        </media-cast-button>
      `
          : ""
      }
      ${
        isdownloadable !== "false" &&
        !renderLiveChrome &&
        mediaItem.media?.downloads?.length
          ? `
        <menu-button customtitle="downloads" id="downloads-menu">
          <svg slot="icon" xmlns="http://www.w3.org/2000/svg" width="20px" height="20px" viewBox="0 0 20 20"><path fill="none" d="M0 0h20v20H0z"/><path fill="currentColor" d="M10 13.575a1.106 1.106 0 0 1-.375-.062.878.878 0 0 1-.325-.212l-3.6-3.6a.916.916 0 0 1-.287-.7 1.054 1.054 0 0 1 .287-.7 1.019 1.019 0 0 1 .713-.312.931.931 0 0 1 .712.287L9 10.15V3a.967.967 0 0 1 .288-.712A.967.967 0 0 1 10 2a.967.967 0 0 1 .712.288A.967.967 0 0 1 11 3v7.15l1.875-1.875a.931.931 0 0 1 .712-.287 1.019 1.019 0 0 1 .712.313 1.054 1.054 0 0 1 .288.7.916.916 0 0 1-.288.7l-3.6 3.6a.878.878 0 0 1-.325.212 1.106 1.106 0 0 1-.374.062ZM4 18a1.926 1.926 0 0 1-1.413-.588A1.926 1.926 0 0 1 2 16v-2a.967.967 0 0 1 .288-.712A.967.967 0 0 1 3 13a.967.967 0 0 1 .712.288A.967.967 0 0 1 4 14v2h12v-2a.967.967 0 0 1 .288-.712A.967.967 0 0 1 17 13a.967.967 0 0 1 .712.288A.967.967 0 0 1 18 14v2a1.926 1.926 0 0 1-.587 1.412A1.926 1.926 0 0 1 16 18Z"/></svg>
        </menu-button>
      `
          : ""
      }
      ${mediaItem.media?.tracks?.captions?.length ? `<media-captions-menu-button></media-captions-menu-button>` : ""}
      ${
        !renderLiveChrome
          ? `
        <media-seek-backward-button seekoffset="10" notooltip>
          <svg slot="icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" ><path fill="none" d="M0 0h20v20H0z" /><path fill="currentColor" d="M7.666 9.83h-.584a.567.567 0 0 1-.418-.165.567.567 0 0 1-.165-.418.567.567 0 0 1 .165-.418.567.567 0 0 1 .418-.165h1.167a.567.567 0 0 1 .418.165.567.567 0 0 1 .165.418v3.5a.567.567 0 0 1-.165.418.567.567 0 0 1-.418.165.567.567 0 0 1-.418-.165.567.567 0 0 1-.165-.418Zm2.723 3.5a.753.753 0 0 1-.554-.224.753.753 0 0 1-.224-.554V9.44a.753.753 0 0 1 .224-.554.753.753 0 0 1 .554-.224h1.556a.753.753 0 0 1 .554.224.753.753 0 0 1 .224.554v3.112a.753.753 0 0 1-.224.554.753.753 0 0 1-.554.224Zm.389-1.167h.778V9.829h-.778ZM9.997 18a6.774 6.774 0 0 1-2.733-.554 7.117 7.117 0 0 1-2.218-1.5 7.118 7.118 0 0 1-1.5-2.218 6.775 6.775 0 0 1-.554-2.733.753.753 0 0 1 .224-.554.753.753 0 0 1 .554-.224.753.753 0 0 1 .554.224.753.753 0 0 1 .224.554 5.255 5.255 0 0 0 1.585 3.861 5.255 5.255 0 0 0 3.864 1.588 5.255 5.255 0 0 0 3.861-1.585 5.255 5.255 0 0 0 1.585-3.861 5.255 5.255 0 0 0-1.585-3.861A5.255 5.255 0 0 0 9.997 5.55H9.88l.661.661a.713.713 0 0 1 .224.545.821.821 0 0 1-.224.545.793.793 0 0 1-.554.243.724.724 0 0 1-.554-.224l-2-2a.746.746 0 0 1-.233-.545.746.746 0 0 1 .233-.545l2-2a.724.724 0 0 1 .554-.224.793.793 0 0 1 .554.243.821.821 0 0 1 .224.545.713.713 0 0 1-.224.545L9.88 4h.117a6.775 6.775 0 0 1 2.733.554 7.117 7.117 0 0 1 2.218 1.5 7.118 7.118 0 0 1 1.5 2.218 6.775 6.775 0 0 1 .554 2.733 6.775 6.775 0 0 1-.554 2.733 7.118 7.118 0 0 1-1.5 2.218 7.118 7.118 0 0 1-2.218 1.5A6.775 6.775 0 0 1 9.997 18Z"/></svg>
        </media-seek-backward-button>
        <media-seek-forward-button seekoffset="10" notooltip>
          <svg slot="icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" ><defs><clipPath id="a"><path fill="currentColor" d="M0 0h14.014v16H0z" /></clipPath></defs><path fill="none" d="M0 0h20v20H0z" data-name="Rectangle 520"/><g data-name="Group 301"><g clip-path="url(#a)" data-name="Group 38" transform="translate(2.993 2)"><path fill="currentColor" d="M7.01 16a6.976 6.976 0 0 1-4.969-2.041 7 7 0 0 1-1.5-2.231A6.9 6.9 0 0 1 0 9a6.9 6.9 0 0 1 .545-2.743 6.971 6.971 0 0 1 3.73-3.726 6.923 6.923 0 0 1 2.743-.545h.43l-.95-.95a.574.574 0 0 1-.176-.425.587.587 0 0 1 .159-.436A.612.612 0 0 1 6.909 0a.578.578 0 0 1 .421.156L9.344 2.17a.729.729 0 0 1 .221.527.715.715 0 0 1-.221.522l-2 1.986a.626.626 0 0 1-.44.193.625.625 0 0 1-.63-.619.6.6 0 0 1 .19-.435l.877-.867h-.417a5.209 5.209 0 0 0-3.874 1.6 5.376 5.376 0 0 0-1.569 3.911A5.323 5.323 0 0 0 3.089 12.9 5.331 5.331 0 0 0 7 14.508a5.345 5.345 0 0 0 3.918-1.6 5.309 5.309 0 0 0 1.61-3.914.713.713 0 0 1 .215-.527.721.721 0 0 1 .529-.214.71.71 0 0 1 .525.214.72.72 0 0 1 .212.527 6.941 6.941 0 0 1-2.041 4.964 7.054 7.054 0 0 1-2.23 1.5A6.878 6.878 0 0 1 7.01 16M4.625 7.513h-.487a.439.439 0 0 1-.338-.147.5.5 0 0 1-.136-.358.427.427 0 0 1 .145-.338.518.518 0 0 1 .354-.127h1.022a.462.462 0 0 1 .345.128.469.469 0 0 1 .125.343v3.975a.49.49 0 0 1-.148.362.5.5 0 0 1-.364.148.5.5 0 0 1-.369-.148.49.49 0 0 1-.149-.362ZM7.469 11.5a.8.8 0 0 1-.586-.235.79.79 0 0 1-.236-.583V7.361a.789.789 0 0 1 .236-.582.8.8 0 0 1 .586-.235h1.753a.8.8 0 0 1 .586.235.788.788 0 0 1 .237.582v3.32a.789.789 0 0 1-.237.583.8.8 0 0 1-.586.235H7.469m.218-.988h1.328v-3H7.687Z" data-name="Path 40"/></g></g></svg>
        </media-seek-forward-button>  
      `
          : ``
      }
      <slot name="premiere-button"></slot>
      </div>
    </div>
  `;

  const posterImage =
    userOptions.overridethumbnail ?? mediaItem.media?.thumbnail;

  const videoMeta = `
    <div class="video-meta">
      ${posterImage ? `<img src="${posterImage}" alt="" />` : ""}
      <div>
        ${userOptions.showtitle === "true" ? `<h1 slot="title">${userOptions.overridetitle ?? mediaItem.title}</h1>` : ""}
        <p slot="description">${userOptions.overridedescription ?? mediaItem.description}</p>
      </div>
    </div>
  `;

  return `
      <media-controller 
      audio
      ${renderLiveChrome ? "islive" : ""}
      ${userOptions.showcontrols === "true" ? "showcontrols" : ""}
      ${userOptions.showcontrols === "false" ? "gesturesdisabled" : ""}
      >
      ${mediaElement}
      <media-captions-menu hidden anchor="auto">
        <svg slot="icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" ><path fill="none" d="M0 0h20v20H0z" /><path fill="currentColor" d="M3.778 17.111a1.712 1.712 0 0 1-1.256-.522A1.712 1.712 0 0 1 2 15.334V4.667a1.712 1.712 0 0 1 .522-1.256 1.712 1.712 0 0 1 1.256-.522h12.444a1.712 1.712 0 0 1 1.256.522A1.712 1.712 0 0 1 18 4.667v10.667a1.712 1.712 0 0 1-.522 1.255 1.712 1.712 0 0 1-1.256.522Zm0-1.778h12.444V4.666H3.778Zm1.778-2.667h2.667a.86.86 0 0 0 .633-.256.86.86 0 0 0 .256-.633v-.444A.452.452 0 0 0 8.979 11a.452.452 0 0 0-.333-.133h-.4a.452.452 0 0 0-.333.133.452.452 0 0 0-.133.333H6V8.666h1.778a.452.452 0 0 0 .133.333.452.452 0 0 0 .333.133h.4a.452.452 0 0 0 .333-.133.452.452 0 0 0 .133-.333v-.444a.86.86 0 0 0-.256-.633.86.86 0 0 0-.633-.255H5.554a.86.86 0 0 0-.633.255.86.86 0 0 0-.256.633v3.556a.86.86 0 0 0 .256.633.86.86 0 0 0 .635.256Zm8.889-5.333h-2.667a.86.86 0 0 0-.633.255.86.86 0 0 0-.256.633v3.556a.86.86 0 0 0 .256.633.86.86 0 0 0 .633.256h2.667a.86.86 0 0 0 .633-.256.86.86 0 0 0 .256-.633v-.444a.452.452 0 0 0-.133-.333.452.452 0 0 0-.333-.133h-.4a.452.452 0 0 0-.333.133.452.452 0 0 0-.133.333h-1.778V8.666H14a.452.452 0 0 0 .133.333.452.452 0 0 0 .333.133h.4a.452.452 0 0 0 .333-.133.452.452 0 0 0 .133-.333v-.444a.86.86 0 0 0-.256-.633.86.86 0 0 0-.632-.256Zm-10.667 8Z"/></svg>
      </media-captions-menu>
      ${mediaSettingsMenu}
      ${shouldShowDescription ? `${videoMeta}` : ""}
      ${
        userOptions.showcontrols !== "false"
          ? `
        <div class="player-background">
          ${controlBar}
        </div>
      `
          : ""
      }
    </media-controller>
  `;
}
export default composeAudioHtml;
